(() => {
  const init = () => {
    const drawer = document.getElementById('CartDrawer');
    const section = drawer?.querySelector('[data-hellsinki-upsell]');
    const products = drawer?.querySelector('[data-products]');
    if (!section || !products) return;

    const countLabel = drawer.querySelector('[data-hellsinki-cart-count]');
    const status = section.querySelector('[data-upsell-status]');
    const collapse = section.querySelector('[data-upsell-collapse]');
    const body = section.querySelector('[data-upsell-body]');
    const track = section.querySelector('[data-upsell-track]');
    const cards = Array.from(section.querySelectorAll('[data-upsell-product-id]'));
    const cartUrl = window.theme?.routes?.cart || '/cart.js';
    const addUrl = window.theme?.routes?.cartAdd || '/cart/add.js';
    const scrollArea = drawer.querySelector('.drawer__inner > .drawer__scrollable');
    let syncVersion = 0;
    let syncQueued = false;
    let resetScrollOnNextBuild = false;
    let drag = null;
    let suppressClickUntil = 0;
    const variantCache = new WeakMap();

    track.querySelectorAll('img').forEach((image) => { image.draggable = false; });
    track.addEventListener('dragstart', (event) => event.preventDefault());
    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0 || event.target.closest('button')) return;
      drag = { x: event.clientX, scrollLeft: track.scrollLeft, moved: false };
    });
    window.addEventListener('pointermove', (event) => {
      if (!drag) return;
      const distance = event.clientX - drag.x;
      if (!drag.moved && Math.abs(distance) < 5) return;
      drag.moved = true;
      track.classList.add('is-dragging');
      track.scrollLeft = drag.scrollLeft - distance;
      event.preventDefault();
    }, { passive: false });
    const endDrag = () => {
      if (drag?.moved) suppressClickUntil = performance.now() + 300;
      drag = null;
      track.classList.remove('is-dragging');
    };
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);
    window.addEventListener('blur', endDrag);
    track.addEventListener('click', (event) => {
      if (performance.now() >= suppressClickUntil) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      suppressClickUntil = 0;
    }, true);

    const getVariants = (card) => {
      if (!variantCache.has(card)) {
        try {
          variantCache.set(card, JSON.parse(card.querySelector('[data-upsell-variants]')?.textContent || '[]'));
        } catch (_) {
          variantCache.set(card, []);
        }
      }
      return variantCache.get(card);
    };

    const getGroups = (card) => Array.from(card.querySelectorAll('[data-upsell-option-group]'));

    const getSelection = (card) => getGroups(card).map((group) => group.querySelector('.is-selected')?.dataset.optionValue || null);

    const refreshChoices = (card) => {
      const variants = getVariants(card);
      const selected = getSelection(card);
      getGroups(card).forEach((group) => {
        const index = Number(group.dataset.optionIndex);
        group.querySelectorAll('[data-upsell-choice]').forEach((choice) => {
          const candidate = selected.slice();
          candidate[index] = choice.dataset.optionValue;
          choice.disabled = !variants.some((variant) => variant.available && variant.options.every((value, position) => !candidate[position] || candidate[position] === value));
        });
      });
    };

    const updateCount = (count) => {
      countLabel.textContent = `${count} ${count === 1 ? 'item' : 'itens'} no carrinho`;
      section.hidden = count === 0 || !cards.some((card) => !card.hidden);
    };

    const updateFromCart = (cart) => {
      const renderedCount = products.querySelector('[data-count]')?.dataset.count;
      const count = Number(renderedCount ?? cart.item_count) || 0;

      const inCart = new Set((cart.items || []).map((item) => String(item.product_id)));
      let visible = 0;
      cards.forEach((card) => {
        card.hidden = inCart.has(card.dataset.upsellProductId);
        if (!card.hidden) visible += 1;
      });
      updateCount(count);
      if (visible === 0) section.hidden = true;
    };

    const syncCart = async () => {
      const version = ++syncVersion;
      try {
        const response = await fetch(cartUrl, { credentials: 'same-origin' });
        if (!response.ok) return;
        const cart = await response.json();
        if (version === syncVersion) updateFromCart(cart);
      } catch (_) {
        const fallbackCount = products.querySelector('[data-count]')?.dataset.count;
        if (fallbackCount) updateCount(Number(fallbackCount));
      }
    };

    const queueSync = () => {
      const renderedCount = products.querySelector('[data-count]')?.dataset.count;
      if (renderedCount) updateCount(Number(renderedCount));
      if (resetScrollOnNextBuild && renderedCount) {
        resetScrollOnNextBuild = false;
        if (scrollArea) scrollArea.scrollTop = 0;
      }
      if (syncQueued) return;
      syncQueued = true;
      window.setTimeout(() => {
        syncQueued = false;
        syncCart();
      }, 40);
    };

    const addVariant = async (card, button, variantId) => {
      if (!variantId || button.disabled) return;
      button.disabled = true;
      status.textContent = 'Adicionando ao carrinho…';

      try {
        const response = await fetch(addUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
        });
        if (!response.ok) throw new Error('Não foi possível adicionar a peça. Tente novamente.');
        status.textContent = 'Peça adicionada ao carrinho.';
        card.querySelector('[data-upsell-picker]').hidden = true;
        card.querySelector('[data-upsell-plus]').setAttribute('aria-expanded', 'false');
        getGroups(card).filter((group) => !group.classList.contains('hellsinki-upsell__option--color') && group.querySelectorAll('[data-upsell-choice]').length > 1).forEach((group) => {
          group.querySelectorAll('[data-upsell-choice]').forEach((choice) => {
            choice.classList.remove('is-selected');
            choice.setAttribute('aria-pressed', 'false');
          });
        });
        resetScrollOnNextBuild = true;
        document.dispatchEvent(new CustomEvent('cart:build'));
      } catch (error) {
        status.textContent = error.message || 'Não foi possível adicionar a peça.';
      } finally {
        button.disabled = false;
      }
    };

    section.addEventListener('click', (event) => {
      const collapseButton = event.target.closest('[data-upsell-collapse]');
      if (collapseButton) {
        const expanded = collapseButton.getAttribute('aria-expanded') === 'true';
        collapseButton.setAttribute('aria-expanded', String(!expanded));
        collapseButton.setAttribute('aria-label', expanded ? 'Expandir sugestões' : 'Recolher sugestões');
        body.classList.toggle('is-collapsed', expanded);
        body.inert = expanded;
        return;
      }

      const plus = event.target.closest('[data-upsell-plus]');
      const choice = event.target.closest('[data-upsell-choice]');
      const card = (plus || choice)?.closest('[data-upsell-product-id]');
      if (!card) return;

      if (choice) {
        const group = choice.closest('[data-upsell-option-group]');
        group.querySelectorAll('[data-upsell-choice]').forEach((item) => {
          const selected = item === choice;
          item.classList.toggle('is-selected', selected);
          item.setAttribute('aria-pressed', String(selected));
        });
        refreshChoices(card);
        const selected = getSelection(card);
        if (selected.every(Boolean)) {
          const variant = getVariants(card).find((item) => item.available && item.options.every((value, index) => value === selected[index]));
          if (variant) addVariant(card, choice, variant.id);
          else status.textContent = 'Essa combinação não está disponível.';
        }
        return;
      }

      const picker = card.querySelector('[data-upsell-picker]');
      const variants = getVariants(card);
      if (variants.length === 1) {
        addVariant(card, plus, variants[0].id);
        return;
      }

      const opening = picker.hidden;
      picker.hidden = !opening;
      plus.setAttribute('aria-expanded', String(opening));
      if (opening) {
        refreshChoices(card);
        window.requestAnimationFrame(() => {
          const overflow = picker.getBoundingClientRect().bottom - scrollArea.getBoundingClientRect().bottom + 14;
          if (overflow > 0) scrollArea.scrollTop += overflow;
        });
      }
    });

    new MutationObserver(queueSync).observe(products, { childList: true });
    document.addEventListener('cart:updated', queueSync);
    syncCart();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
